/**
 * CandidateDataDrawer - Bottom drawer displaying candidate data tables
 *
 * Shows foundation data (Dates, Taxable Values, Existing Debt) and candidate data (Cash Flow, Tax Year Plan) in tabs with three states:
 * - Collapsed: Tab buttons only
 * - Normal (preview): Tabs + table content preview
 * - Full: Full table content
 */

import { Box, Button, ButtonGroup, HStack, Text, VStack } from '@chakra-ui/react';
import { AlertCircle, AlertTriangle, Edit, FileSpreadsheet } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BondSeriesResultsTable } from '~/builders/bond-series/BondSeriesResultsTable';
import { CashFlowsTable } from '~/builders/cash-flows/CashFlowsTable';
import { ExistingDebtCumulativeTable } from '~/builders/existing-debt/ExistingDebtCumulativeTable';
import { ExistingDebtTable } from '~/builders/existing-debt/ExistingDebtTable';
import { DateTable } from '~/builders/project-dates/DateTable';
import { RevenueMapTable } from '~/builders/revenue-map/RevenueMapTable';
import { TaxYearPlanTable } from '~/builders/tax-year-plan/TaxYearPlanTable';
import { TaxableValueBuilder } from '~/builders/taxable-value/TaxableValueBuilder';
import { TaxableValueTable } from '~/builders/taxable-value/TaxableValueTable';
import {
  BottomDrawer,
  type DrawerState,
  type DrawerTab,
} from '~/components/layout/page-utilities/BottomDrawer';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { useDateMaps } from '~/hooks/useDateMaps';
import type { ProjectData } from '~/hooks/useProjectStore';
import { exportToExcel } from '~/services/excel-export-service';
import type { Candidate, Configuration, DateRecord, TaxValueInputs } from '~/types';
import type { MillageOverrides, TaxableValueOverrides } from '~/types/overrides';
import { parseFormattedNumber } from '~/utils/numberParsing';

import { MillageOverrideModal } from './components/MillageOverrideModal';
import { TaxableValueOverrideModal } from './components/TaxableValueOverrideModal';

export interface CandidateDataDrawerProps {
  /** Drawer state: collapsed, preview (normal), or full */
  state: DrawerState;

  /** Currently selected candidate */
  candidate: Candidate | null;

  /** Project data for foundation data display */
  project: ProjectData;

  /** Configuration data for bond series and DRF inputs */
  configuration: Configuration;

  /** Handler called when drawer state changes */
  onToggleState: (state: DrawerState) => void;

  /** Handler called when user submits taxable value overrides */
  onCustomizeTaxableValues?: (overrides: TaxableValueOverrides) => void;

  /** Handler called when user submits millage rate overrides */
  onCustomizeMillageRates?: (overrides: MillageOverrides) => void;
}

type DrawerTabId =
  | 'dates'
  | 'taxvalues'
  | 'debt'
  | 'bonds'
  | 'cashflow'
  | 'taxyear'
  | 'revenuemap'
  | 'information';

export const CandidateDataDrawer: React.FC<CandidateDataDrawerProps> = ({
  state,
  candidate,
  project,
  configuration,
  onToggleState,
  onCustomizeTaxableValues,
  onCustomizeMillageRates,
}) => {
  const [activeTab, setActiveTab] = useState<DrawerTabId>('dates');
  const [viewportHeight, setViewportHeight] = useState<number>(800); // Default fallback
  const [selectedDebtProjectIndex, setSelectedDebtProjectIndex] = useState<number | null>(null); // null = "All Projects"
  const [selectedBondIndex, setSelectedBondIndex] = useState<number>(0);
  // Taxable value override modal state
  const [isTaxableValueModalOpen, setIsTaxableValueModalOpen] = useState(false);
  // Millage override modal state
  const [isMillageModalOpen, setIsMillageModalOpen] = useState(false);

  // Get viewport height for responsive drawer sizing
  useEffect(() => {
    const updateHeight = () => {
      setViewportHeight(window.innerHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Get date records and inputs from project
  const dateRecords = project.dateRecords || project.dateElements?.dateRecords || null;
  const dateInputs = project.dateInputs;

  // Convert form inputs to engine format for taxable value override modal
  const taxValueInputs: TaxValueInputs | null = useMemo(() => {
    const formInputs = project.taxValueInputs;
    if (!formInputs) return null;

    try {
      return {
        startingYearTaxableValue: parseFormattedNumber(formInputs.initialTaxableValue),
        startingYearGrowthRate: Number.parseFloat(formInputs.startYearGrowthRate) / 100, // Convert percentage to decimal
        fiveYearRate: Number.parseFloat(formInputs.fiveYearGrowthRate) / 100,
        twentyYearRate: Number.parseFloat(formInputs.twentyYearGrowthRate) / 100,
      };
    } catch {
      return null;
    }
  }, [project.taxValueInputs]);

  // Get starting tax year from date inputs or taxable value map
  const startingTaxYear: number = useMemo(() => {
    // First try from date inputs
    if (dateInputs?.startingTaxYear) {
      const parsed = Number.parseInt(dateInputs.startingTaxYear, 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
    // Fall back to first year in taxable value map
    if (project.taxableValueMap) {
      const years = Object.keys(project.taxableValueMap)
        .map(Number)
        .sort((a, b) => a - b);
      if (years.length > 0) return years[0];
    }
    // Default to current year
    return new Date().getFullYear();
  }, [dateInputs?.startingTaxYear, project.taxableValueMap]);

  // Generate date map formats for DateTable (only when expanded and dates tab is selected)
  const dateMapsResult = useDateMaps(
    dateRecords,
    dateInputs,
    state !== 'collapsed' && activeTab === 'dates'
  );
  const dateMapsByTaxYear = dateMapsResult.byTaxYear;
  const dateMapsByPaymentDate = dateMapsResult.byPaymentDate;

  // Foundation data table components using existing builder components
  // DateTable expects DateRecord[] with Record<string, unknown> index signature
  // Cast to satisfy the type constraint (runtime structure is compatible)
  const DatesTableContent = () => (
    <DateTable
      data={(dateRecords as unknown as Array<DateRecord & Record<string, unknown>>) || undefined}
      dateRecordsMapByTaxYear={dateMapsByTaxYear || undefined}
      dateRecordsMapByPaymentDate={dateMapsByPaymentDate || undefined}
      maxRows={LAYOUT_SPACING.TABLE_DEFAULT_ROWS}
    />
  );

  // Calculate taxable value map with overrides applied (if present)
  const taxableValueMapWithOverrides = useMemo(() => {
    // If no overrides, use the base map
    if (!configuration.overrides?.taxableValue || !project.taxValueInputs) {
      return project.taxableValueMap;
    }

    // Recalculate with overrides - similar to RevenueMapBuilder.resolveTaxableValueMap
    const taxValueInputs = {
      startingYearTaxableValue: parseFormattedNumber(project.taxValueInputs.initialTaxableValue),
      startingYearGrowthRate: Number.parseFloat(project.taxValueInputs.startYearGrowthRate) / 100,
      fiveYearRate: Number.parseFloat(project.taxValueInputs.fiveYearGrowthRate) / 100,
      twentyYearRate: Number.parseFloat(project.taxValueInputs.twentyYearGrowthRate) / 100,
    };

    const taxYears = Object.keys(project.taxableValueMap || {})
      .map(Number)
      .sort((a, b) => a - b);
    if (taxYears.length === 0) {
      return project.taxableValueMap;
    }
    const startingTaxYear = taxYears[0];
    const yearsToProject = taxYears.length;

    const rebuiltEntries = TaxableValueBuilder.buildWithFixedYears(
      taxValueInputs,
      startingTaxYear,
      yearsToProject,
      configuration.overrides.taxableValue
    );

    // Convert array to map format
    const result: Record<number, { taxYear: number; taxableValue: number; growthRate: number }> =
      {};
    for (const entry of rebuiltEntries) {
      result[entry.taxYear] = entry;
    }

    return result;
  }, [project.taxableValueMap, project.taxValueInputs, configuration.overrides?.taxableValue]);

  const TaxableValuesTableContent = () => (
    <VStack align="stretch" gap={2} p={4}>
      {/* Header with Customize button */}
      <HStack justify="space-between" mb={2}>
        <Text fontSize="sm" fontWeight="medium" color="gray.700">
          Taxable Value Projections
        </Text>
        {onCustomizeTaxableValues && (
          <Button
            size="xs"
            variant="outline"
            colorPalette="blue"
            onClick={() => setIsTaxableValueModalOpen(true)}
          >
            <Edit size={12} />
            <Text ml={1}>Customize</Text>
          </Button>
        )}
      </HStack>
      <TaxableValueTable
        taxableValueMap={taxableValueMapWithOverrides}
        overrides={configuration.overrides?.taxableValue}
      />
    </VStack>
  );

  const ExistingDebtTableContent = () => {
    const debtProjects = project.existingDebtProjects || [];

    if (debtProjects.length === 0) {
      return (
        <Box p={4} borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="gray.50">
          <Text fontSize="sm" color="gray.600" textAlign="center">
            No existing debt projects configured
          </Text>
          <Text fontSize="xs" color="gray.500" textAlign="center" mt={1}>
            This is valid - not all projects have existing debt.
          </Text>
        </Box>
      );
    }

    // Single debt project - show cumulative table
    if (debtProjects.length === 1) {
      return (
        <ExistingDebtCumulativeTable
          existingDebtProjects={debtProjects}
          dateInputs={dateInputs || undefined}
          dateRecords={dateRecords || undefined}
        />
      );
    }

    // Multiple debt projects - show buttons to toggle between views (one at a time)
    return (
      <VStack align="stretch" gap={4}>
        {/* Selection buttons */}
        <Box>
          <HStack gap={2} wrap="wrap">
            <Button
              size="sm"
              variant={selectedDebtProjectIndex === null ? 'solid' : 'outline'}
              colorPalette="blue"
              onClick={() => setSelectedDebtProjectIndex(null)}
            >
              All Projects
            </Button>
            {debtProjects.map((debt, index) => (
              <Button
                key={debt.id}
                size="sm"
                variant={selectedDebtProjectIndex === index ? 'solid' : 'outline'}
                colorPalette="blue"
                onClick={() => setSelectedDebtProjectIndex(index)}
              >
                {debt.name}
              </Button>
            ))}
          </HStack>
        </Box>

        {/* Show selected view */}
        {selectedDebtProjectIndex === null ? (
          <ExistingDebtCumulativeTable
            existingDebtProjects={debtProjects}
            dateInputs={dateInputs || undefined}
            dateRecords={dateRecords || undefined}
          />
        ) : (
          <ExistingDebtTable
            existingDebtProjects={[debtProjects[selectedDebtProjectIndex]]}
            showSummary={false}
          />
        )}
      </VStack>
    );
  };

  const BondsTableContent = () => {
    if (!candidate) {
      return (
        <Box p={4}>
          <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
            Select a candidate to view bond payment schedules
          </Text>
        </Box>
      );
    }

    const bondSeries = configuration.bondSeries || [];

    if (bondSeries.length === 0) {
      return (
        <Box p={4}>
          <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
            No bond series available
          </Text>
        </Box>
      );
    }

    // Single bond - show directly
    if (bondSeries.length === 1) {
      return (
        <Box p={4}>
          <BondSeriesResultsTable
            key={bondSeries[0].id}
            bondSeriesId={bondSeries[0].id}
            bondSeriesName={bondSeries[0].name}
            cashFlows={candidate.cashFlows || []}
            title={bondSeries[0].name}
          />
        </Box>
      );
    }

    // Multiple bonds - show buttons to toggle between them
    const selectedBond = bondSeries[selectedBondIndex];

    if (!selectedBond) {
      return <Text>No bond selected</Text>;
    }

    return (
      <VStack align="stretch" gap={4}>
        {/* Selection buttons */}
        <Box px={4} pt={4}>
          <HStack gap={2} wrap="wrap">
            {bondSeries.map((bond, index) => (
              <Button
                key={bond.id}
                size="sm"
                variant={selectedBondIndex === index ? 'solid' : 'outline'}
                colorPalette="blue"
                onClick={() => setSelectedBondIndex(index)}
              >
                {bond.name}
              </Button>
            ))}
          </HStack>
        </Box>

        {/* Show selected bond */}
        <Box p={4}>
          <BondSeriesResultsTable
            key={selectedBond.id}
            bondSeriesId={selectedBond.id}
            bondSeriesName={selectedBond.name}
            cashFlows={candidate.cashFlows || []}
            title={selectedBond.name}
          />
        </Box>
      </VStack>
    );
  };

  // Candidate data table components (only show content when candidate selected)
  const CashFlowTable = () => {
    if (!candidate) {
      return (
        <Box p={4}>
          <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
            Select a candidate to view details
          </Text>
        </Box>
      );
    }

    return (
      <Box p={4}>
        <CashFlowsTable cashFlows={candidate.cashFlows || []} />
      </Box>
    );
  };

  const TaxYearTable = () => {
    if (!candidate) {
      return (
        <Box p={4}>
          <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
            Select a candidate to view details
          </Text>
        </Box>
      );
    }

    // Get bond names from configuration for per-bond payment columns
    const bondNames = configuration.bondSeries?.map((bond) => bond.name) || [];

    // Get millage shape name from candidate
    const millageShapeName = candidate.millageInformation?.millageShape?.shapeName;

    return (
      <Box p={4}>
        <TaxYearPlanTable
          taxYearPlan={candidate.taxYearBasedProjectPlan || []}
          showSummary={true}
          bondNames={bondNames}
          projectName={project.metadata.name}
          configurationName={configuration.name}
          millageShapeName={millageShapeName}
        />
      </Box>
    );
  };

  // Revenue Map Table Content
  const RevenueMapTableContent = () => {
    if (!candidate) {
      return (
        <Box p={4}>
          <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
            Select a candidate to view revenue allocation
          </Text>
        </Box>
      );
    }

    if (!candidate.revenueMap) {
      return (
        <Box p={4}>
          <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
            No revenue map data available for this candidate
          </Text>
        </Box>
      );
    }

    // Extract millage map from revenue map for the modal
    const millageMap: Record<number, number> = {};
    for (const entry of Object.values(candidate.revenueMap)) {
      millageMap[entry.taxYear] = entry.millage;
    }

    const millageShape = candidate.millageInformation?.millageShape;

    return (
      <VStack align="stretch" gap={2} p={4}>
        {/* Header with Customize button */}
        <HStack justify="space-between" mb={2}>
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            Revenue Map - {millageShape?.shapeName || 'Unknown Shape'}
          </Text>
          {onCustomizeMillageRates && (
            <Button
              size="xs"
              variant="outline"
              colorPalette="purple"
              onClick={() => setIsMillageModalOpen(true)}
            >
              <Edit size={12} />
              <Text ml={1}>Customize Rates</Text>
            </Button>
          )}
        </HStack>
        <RevenueMapTable revenueMap={candidate.revenueMap} />
      </VStack>
    );
  };

  // Information Content - Always visible, shows candidate status, warnings/failures, and optimization history
  const InformationContent = () => {
    if (!candidate) {
      return (
        <Box p={4}>
          <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
            Select a candidate to view information
          </Text>
        </Box>
      );
    }

    const failureMessages = candidate.results.failureMessages || [];
    const isViable = candidate.results.isViable;
    const optimizationHistory = candidate.optimizationHistory || [];

    // Map optimization type to display name
    const getOptimizationDisplayName = (type: string): string => {
      switch (type) {
        case 'initial-shape':
          return 'Initial Shape';
        case 'optimization-skipped':
          return 'Optimization Check';
        case 'utilization':
          return 'Utilization Optimization';
        case 'five-year-fix':
          return 'Five-Year Rule Fix';
        case 'millage-adjustment':
          return 'Millage Adjustment';
        case 'target-compliance':
          return 'Target Compliance';
        default:
          return type;
      }
    };

    // Get styling for each entry type
    const getEntryStyle = (
      type: string
    ): { bg: string; borderColor: string; textColor: string; labelColor: string } => {
      switch (type) {
        case 'initial-shape':
          return {
            bg: 'gray.50',
            borderColor: 'gray.200',
            textColor: 'gray.600',
            labelColor: 'gray.700',
          };
        case 'optimization-skipped':
          return {
            bg: 'orange.50',
            borderColor: 'orange.200',
            textColor: 'orange.600',
            labelColor: 'orange.700',
          };
        default:
          // Applied optimizations
          return {
            bg: 'blue.50',
            borderColor: 'blue.200',
            textColor: 'blue.700',
            labelColor: 'blue.800',
          };
      }
    };

    return (
      <Box p={4}>
        <VStack align="stretch" gap={6}>
          {/* Candidate Status Section */}
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
              {candidate.name}
            </Text>
            <Box
              p={3}
              borderRadius="md"
              bg={isViable ? 'green.50' : 'red.50'}
              borderWidth="1px"
              borderColor={isViable ? 'green.200' : 'red.200'}
            >
              <Text fontSize="sm" color={isViable ? 'green.800' : 'red.800'}>
                {isViable ? '✓ Viable candidate' : '✗ Non-viable candidate'}
              </Text>
            </Box>
          </Box>

          {/* Warnings/Failures Section - Only if messages exist */}
          {failureMessages.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                {isViable ? 'Warnings' : 'Failures'}
              </Text>
              <VStack align="stretch" gap={2}>
                {failureMessages.map((message) => (
                  <Box
                    key={message}
                    p={3}
                    borderRadius="md"
                    bg={isViable ? 'yellow.50' : 'red.50'}
                    borderWidth="1px"
                    borderColor={isViable ? 'yellow.200' : 'red.200'}
                  >
                    <HStack gap={2} align="flex-start">
                      {isViable ? (
                        <AlertCircle
                          size={16}
                          color="#D69E2E"
                          style={{ flexShrink: 0, marginTop: '2px' }}
                        />
                      ) : (
                        <AlertTriangle
                          size={16}
                          color="#E53E3E"
                          style={{ flexShrink: 0, marginTop: '2px' }}
                        />
                      )}
                      <Text fontSize="sm" color={isViable ? 'yellow.800' : 'red.800'}>
                        {message}
                      </Text>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {/* Optimization History Section */}
          <Box>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
              Candidate Journey
            </Text>
            {optimizationHistory.length === 0 ? (
              <Box p={3} borderRadius="md" bg="gray.50" borderWidth="1px" borderColor="gray.200">
                <Text fontSize="sm" color="gray.600">
                  No history recorded.
                </Text>
              </Box>
            ) : (
              <VStack align="stretch" gap={2}>
                {optimizationHistory.map((entry, index) => {
                  const style = getEntryStyle(entry.type);
                  return (
                    <Box
                      key={`${entry.type}-${entry.timestamp}-${index}`}
                      p={3}
                      borderRadius="md"
                      bg={style.bg}
                      borderWidth="1px"
                      borderColor={style.borderColor}
                    >
                      <Text fontSize="sm" color={style.labelColor} fontWeight="medium" mb={1}>
                        {getOptimizationDisplayName(entry.type)}
                      </Text>
                      <Text fontSize="xs" color={style.textColor}>
                        {entry.description}
                      </Text>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>
        </VStack>
      </Box>
    );
  };

  // Define drawer tabs - Project tabs first, then Candidate tabs
  // Project tabs: Dates, Taxable Values, Existing Debt
  // Candidate tabs: Bonds, Cash Flow, Tax Year Plan, Revenue Map, Information (always present)
  // IMPORTANT: Use render functions to enable lazy rendering (only active tab renders)
  const projectTabs: DrawerTab<DrawerTabId>[] = [
    {
      id: 'dates',
      label: 'Dates',
      render: () => <DatesTableContent />,
    },
    {
      id: 'taxvalues',
      label: 'Taxable Values',
      render: () => <TaxableValuesTableContent />,
    },
    {
      id: 'debt',
      label: 'Existing Debt',
      render: () => <ExistingDebtTableContent />,
    },
  ];

  const candidateTabs: DrawerTab<DrawerTabId>[] = [
    {
      id: 'bonds',
      label: 'Bonds',
      render: () => <BondsTableContent />,
    },
    {
      id: 'cashflow',
      label: 'Cash Flow',
      render: () => <CashFlowTable />,
    },
    {
      id: 'taxyear',
      label: 'Tax Year Plan',
      render: () => <TaxYearTable />,
    },
    {
      id: 'revenuemap',
      label: 'Revenue Map',
      render: () => <RevenueMapTableContent />,
    },
    {
      id: 'information',
      label: 'Information',
      render: () => <InformationContent />,
    },
  ];

  // Combine tabs with visual separator
  // We'll add a separator by using a special tab ID, but BottomDrawer doesn't support that
  // So we'll just combine them and handle visual separation in rendering if needed
  const drawerTabs: DrawerTab<DrawerTabId>[] = [...projectTabs, ...candidateTabs];

  // Handle tab change - expand to half when tab is clicked
  const handleTabChange = (tabId: DrawerTabId) => {
    setActiveTab(tabId);
    // If collapsed, expand to half when tab is clicked
    if (state === 'collapsed') {
      onToggleState('preview');
    }
  };

  // Warnings/Failures button logic
  const renderWarningsButton = () => {
    if (!candidate) return null;

    const failureMessages = candidate.results.failureMessages || [];
    const isViable = candidate.results.isViable;

    if (!isViable && failureMessages.length > 0) {
      // Red button - failures exist
      return (
        <Button
          size="sm"
          colorPalette="red"
          onClick={() => {
            onToggleState('full');
            setActiveTab('information');
          }}
        >
          <HStack gap={1}>
            <AlertTriangle size={16} />
            <Text>
              {failureMessages.length} {failureMessages.length === 1 ? 'Failure' : 'Failures'}
            </Text>
          </HStack>
        </Button>
      );
    }

    if (isViable && failureMessages.length > 0) {
      // Yellow button - warnings exist
      return (
        <Button
          size="sm"
          colorPalette="yellow"
          onClick={() => {
            onToggleState('full');
            setActiveTab('information');
          }}
        >
          <HStack gap={1}>
            <AlertCircle size={16} />
            <Text>
              {failureMessages.length} {failureMessages.length === 1 ? 'Warning' : 'Warnings'}
            </Text>
          </HStack>
        </Button>
      );
    }

    return null;
  };

  // Handle Excel export
  const handleExportExcel = useCallback(() => {
    const millageShapeName = candidate?.millageInformation?.millageShape?.shapeName;

    exportToExcel(project, configuration, candidate, {
      projectName: project.metadata.name,
      configurationName: configuration.name,
      millageShapeName,
    });
  }, [project, configuration, candidate]);

  // Custom action buttons: Export, Warnings button (if applicable), then Close, Half, and Full
  const customActions = (
    <HStack gap={2}>
      <Button size="sm" variant="outline" onClick={handleExportExcel} colorPalette="green">
        <FileSpreadsheet size={14} />
        <Text ml={1}>Export Excel</Text>
      </Button>
      {renderWarningsButton()}
      <ButtonGroup size="sm" variant="outline" attached>
        <Button
          variant={state === 'preview' ? 'solid' : 'outline'}
          colorPalette={state === 'preview' ? 'blue' : undefined}
          onClick={() => onToggleState('preview')}
          disabled={state === 'preview'}
        >
          Half
        </Button>
        <Button
          variant={state === 'full' ? 'solid' : 'outline'}
          colorPalette={state === 'full' ? 'blue' : undefined}
          onClick={() => onToggleState('full')}
          disabled={state === 'full'}
        >
          Full
        </Button>
        <Button
          variant={state === 'collapsed' ? 'solid' : 'outline'}
          colorPalette={state === 'collapsed' ? 'blue' : undefined}
          onClick={() => onToggleState('collapsed')}
          disabled={state === 'collapsed'}
        >
          Close
        </Button>
      </ButtonGroup>
    </HStack>
  );

  // Calculate heights: Half = 50vh, Full = viewport - header space (100px)
  const halfHeight = Math.floor(viewportHeight / 2);
  const fullHeight = viewportHeight - 100; // Leave space for header

  // Project tabs always show content; candidate tabs need candidate selected
  const isProjectTab = activeTab === 'dates' || activeTab === 'taxvalues' || activeTab === 'debt';
  const isCandidateTab =
    activeTab === 'bonds' ||
    activeTab === 'cashflow' ||
    activeTab === 'taxyear' ||
    activeTab === 'revenuemap' ||
    activeTab === 'information';
  const previewMessage = isProjectTab
    ? null // Project tabs show their content in preview
    : isCandidateTab && candidate
      ? null // Candidate tabs show content if candidate selected
      : isCandidateTab
        ? 'Select a candidate to view details' // Candidate tabs without candidate
        : null;

  // Handle taxable value override submission
  const handleTaxableValueOverrideSubmit = useCallback(
    (overrides: TaxableValueOverrides) => {
      if (onCustomizeTaxableValues) {
        onCustomizeTaxableValues(overrides);
      }
    },
    [onCustomizeTaxableValues]
  );

  // Handle millage override submission
  const handleMillageOverrideSubmit = useCallback(
    (overrides: MillageOverrides) => {
      if (onCustomizeMillageRates) {
        onCustomizeMillageRates(overrides);
      }
    },
    [onCustomizeMillageRates]
  );

  // Get millage data for the modal
  const millageMap: Record<number, number> | null = candidate?.revenueMap
    ? Object.fromEntries(
        Object.values(candidate.revenueMap).map((entry) => [entry.taxYear, entry.millage])
      )
    : null;
  const millageShape = candidate?.millageInformation?.millageShape;

  return (
    <>
      <BottomDrawer
        state={state}
        tabs={drawerTabs}
        activeTabId={activeTab}
        onStateChange={onToggleState}
        onTabChange={handleTabChange}
        previewHeight={halfHeight}
        collapsedHeight={LAYOUT_SPACING.DRAWER_COLLAPSED}
        minHeight={halfHeight}
        maxHeight={fullHeight}
        previewMessage={previewMessage}
        customActions={customActions}
        tabSeparatorIndex={projectTabs.length}
      />

      {/* Taxable Value Override Modal */}
      {taxValueInputs && (
        <TaxableValueOverrideModal
          isOpen={isTaxableValueModalOpen}
          onClose={() => setIsTaxableValueModalOpen(false)}
          onSubmit={handleTaxableValueOverrideSubmit}
          taxValueInputs={taxValueInputs}
          startingTaxYear={startingTaxYear}
          existingOverrides={configuration.overrides?.taxableValue}
        />
      )}

      {/* Millage Override Modal */}
      <MillageOverrideModal
        isOpen={isMillageModalOpen}
        onClose={() => setIsMillageModalOpen(false)}
        onSubmit={handleMillageOverrideSubmit}
        millageMap={millageMap}
        sourceShapeId={millageShape?.shapeId}
        sourceShapeName={millageShape?.shapeName}
        existingOverrides={configuration.overrides?.millage}
        peakMillage={
          configuration.millageInputs?.peakMillage
            ? Number.parseFloat(configuration.millageInputs.peakMillage)
            : undefined
        }
      />
    </>
  );
};
